import * as THREE from 'heritage/assets/three.module.js';

const V=(x=0,y=0,z=0)=>new THREE.Vector3(x,y,z);
const cross=(a,b,c)=>(b.x-a.x)*(c.y-a.y)-(b.y-a.y)*(c.x-a.x);
function convexHull(points){
  const sorted=[...points].sort((a,b)=>a.x-b.x||a.y-b.y);
  function half(ps){const out=[];for(const p of ps){while(out.length>1&&cross(out.at(-2),out.at(-1),p)<=1e-10)out.pop();out.push(p);}return out;}
  return [...half(sorted).slice(0,-1),...half([...sorted].reverse()).slice(0,-1)];
}
function contains(p,ring){
  let yes=false;
  for(let i=0,j=ring.length-1;i<ring.length;j=i++){
    const a=ring[i],b=ring[j];
    if((a.y>p.y)!==(b.y>p.y)&&p.x<(b.x-a.x)*(p.y-a.y)/(b.y-a.y)+a.x)yes=!yes;
  }
  return yes;
}
const mean=points=>points.reduce((s,p)=>s.add(p),new THREE.Vector2()).divideScalar(points.length);
function samplePerimeter(points,count){
  const lengths=points.map((p,i)=>p.distanceTo(points[(i+1)%points.length]));
  const total=lengths.reduce((a,b)=>a+b,0),out=[];
  for(let i=0;i<count;i++){
    let distance=i/count*total,j=0;
    while(distance>lengths[j]&&j<lengths.length-1)distance-=lengths[j++];
    out.push(points[j].clone().lerp(points[(j+1)%points.length],distance/lengths[j]));
  }
  return out;
}

// Broad custom cuts follow the actual bee contours. Convex body sections can
// become one stone; curved or divided wings use a short row of fitted cuts.
export function designDiamondCuts(shape,center){
  const extracted=shape.extractPoints(24);
  const map=ring=>ring.map(p=>new THREE.Vector2((p.x-40)*.039-center.x,(38.6219-p.y)*.039-center.y));
  const rings=[map(extracted.shape),...extracted.holes.map(map)],cuts=[];
  const isInside=p=>contains(p,rings[0])&&!rings.slice(1).some(r=>contains(p,r));
  const insideEdges=polygon=>polygon.every((a,i)=>{
    const b=polygon[(i+1)%polygon.length],steps=Math.max(2,Math.ceil(a.distanceTo(b)/.004));
    for(let j=0;j<=steps;j++)if(!isInside(a.clone().lerp(b,j/steps)))return false;
    return true;
  });
  function fit(points,large=false){
    if(points.length<3)return null;
    const hull=convexHull(points);if(hull.length<3)return null;
    const polygon=samplePerimeter(hull,large?16:10),c=mean(polygon);
    for(const scale of [large?.988:.992,.98,.96,.935]){
      const contour=polygon.map(p=>p.clone().sub(c).multiplyScalar(scale).add(c));
      if(insideEdges(contour))return contour;
    }
    return null;
  }
  const hull=convexHull(rings[0]);
  const area=Math.abs(THREE.ShapeUtils.area(rings[0])),hullArea=Math.abs(THREE.ShapeUtils.area(hull));
  const whole=!rings[1]&&hullArea<area*1.035?fit(hull,true):null;
  if(whole){cuts.push(whole);return {cuts,rings,isInside};}

  const c=mean(rings[0]);let xx=0,yy=0,xy=0;
  for(const p of rings[0]){const x=p.x-c.x,y=p.y-c.y;xx+=x*x;yy+=y*y;xy+=x*y;}
  const angle=.5*Math.atan2(2*xy,xx-yy),co=Math.cos(angle),si=Math.sin(angle);
  const local=rings.map(r=>r.map(p=>new THREE.Vector2(p.x*co+p.y*si,-p.x*si+p.y*co)));
  const box=new THREE.Box2().setFromPoints(local[0]);
  const count=Math.max(2,Math.ceil((box.max.x-box.min.x)/.15));
  const step=(box.max.x-box.min.x)/count;
  function intervals(u){
    const values=[];
    for(const ring of local)for(let i=0,j=ring.length-1;i<ring.length;j=i++){
      const a=ring[i],b=ring[j];
      if((a.x>u)!==(b.x>u))values.push(a.y+(b.y-a.y)*(u-a.x)/(b.x-a.x));
    }
    values.sort((a,b)=>a-b);const result=[];
    for(let i=0;i+1<values.length;i+=2)result.push([values[i],values[i+1]]);
    return result;
  }
  const world=(u,v)=>new THREE.Vector2(u*co-v*si,u*si+v*co);
  function section(start,end,depth=0){
    const mid=(start+end)/2,pending=[];let failed=false;
    for(const interval of intervals(mid)){
      const vm=(interval[0]+interval[1])/2,points=[];
      for(const t of [.025,.08,.2,.4,.6,.8,.92,.975]){
        const u=start+(end-start)*t;
        const choices=intervals(u);
        const match=choices.sort((a,b)=>Math.abs((a[0]+a[1])/2-vm)-Math.abs((b[0]+b[1])/2-vm))[0];
        if(!match||match[1]-match[0]<.003)continue;
        const margin=Math.min(.001,(match[1]-match[0])*.04);
        points.push(world(u,match[0]+margin),world(u,match[1]-margin));
      }
      const contour=fit(points);
      if(contour&&Math.abs(THREE.ShapeUtils.area(contour))>.000035)pending.push(contour);
      else failed=true;
    }
    if(failed&&depth<4){section(start,mid,depth+1);section(mid,end,depth+1);}
    else cuts.push(...pending);
  }
  for(let i=0;i<count;i++)section(box.min.x+i*step,box.min.x+(i+1)*step);
  return {cuts,rings,isInside};
}

export function buildShapedCut(contour){
  const center=mean(contour),local=contour.map(p=>p.clone().sub(center));
  const box=new THREE.Box2().setFromPoints(local),size=box.getSize(new THREE.Vector2());
  // Thickness follows the narrow dimension rather than creating bulbous stones.
  const depth=Math.max(.007,Math.min(size.x,size.y)*.48),height=depth*.38;
  const positions=[],normals=[],planes=[],seen=new Set(),n=local.length;
  const point=(i,scale,z)=>V(local[(i+n)%n].x*scale,local[(i+n)%n].y*scale,z);
  function face(a,b,c){
    const normal=b.clone().sub(a).cross(c.clone().sub(a)).normalize();
    if(normal.dot(a.clone().add(b).add(c).multiplyScalar(1/3).sub(V(0,0,-depth*.2)))<0){[b,c]=[c,b];normal.negate();}
    positions.push(...a.toArray(),...b.toArray(),...c.toArray());
    for(let i=0;i<3;i++)normals.push(...normal.toArray());
    const plane=[...normal.toArray(),-normal.dot(a)],key=plane.map(x=>x.toFixed(6)).join(',');
    if(!seen.has(key)){planes.push(plane);seen.add(key);}
  }
  for(let i=0;i<n;i++){
    face(V(0,0,height),point(i,.48,height),point(i+1,.48,height));
    const rings=[[.48,height],[.82,height*.44],[1,0],[.58,-depth*.5]];
    for(let j=0;j<rings.length-1;j++){
      const [r,z]=rings[j],[s,w]=rings[j+1];
      face(point(i,r,z),point(i,s,w),point(i+1,s,w));
      face(point(i,r,z),point(i+1,s,w),point(i+1,r,z));
    }
    face(point(i,.58,-depth*.5),V(0,0,-depth),point(i+1,.58,-depth*.5));
  }
  const geometry=new THREE.BufferGeometry();geometry.setAttribute('position',new THREE.Float32BufferAttribute(positions,3));geometry.setAttribute('normal',new THREE.Float32BufferAttribute(normals,3));
  geometry.computeBoundingBox();geometry.computeBoundingSphere();
  return {geometry,planes,center,depth,height};
}

const MAX_PLANES=72;
function crystalMaterial(cut){
  const padded=Array.from({length:MAX_PLANES},(_,i)=>new THREE.Vector4(...(cut.planes[i]??[0,0,0,0])));
  const material=new THREE.ShaderMaterial({
    transparent:true,depthWrite:true,side:THREE.FrontSide,
    uniforms:{uPlanes:{value:padded},uPlaneCount:{value:cut.planes.length},uInverse:{value:new THREE.Matrix4()},uDirection:{value:new THREE.Matrix3()},uOpacity:{value:0},uEpsilon:{value:cut.depth*.0005}},
    vertexShader:`
      varying vec3 vPosition,vNormal;
      void main(){vPosition=position;vNormal=normal;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
    fragmentShader:`
      uniform vec4 uPlanes[72];
      uniform int uPlaneCount;
      uniform mat4 uInverse;
      uniform mat3 uDirection;
      uniform float uOpacity,uEpsilon;
      varying vec3 vPosition,vNormal;
      vec3 studio(vec3 ray){
        vec3 d=normalize(uDirection*ray);
        vec3 light=mix(vec3(.035,.045,.065),vec3(.42,.47,.54),smoothstep(-.4,.7,d.y));
        light+=vec3(2.0)*smoothstep(.84,.88,dot(d,normalize(vec3(-.8,.4,.55))));
        light+=vec3(1.5,1.55,1.65)*smoothstep(.90,.93,dot(d,normalize(vec3(.8,.6,-.35))));
        light+=vec3(1.3)*smoothstep(.89,.92,d.y);
        float darkCard=smoothstep(.77,.85,dot(d,normalize(vec3(.1,-.2,1.0))));
        return mix(light,vec3(.014,.02,.03),darkCard*.96);
      }
      float fresnel(float c){return .1724+.8276*pow(1.0-clamp(c,0.0,1.0),5.0);}
      void main(){
        vec3 eye=(uInverse*vec4(cameraPosition,1.0)).xyz;
        vec3 incoming=normalize(vPosition-eye),normal=normalize(vNormal);
        float entrance=fresnel(dot(-incoming,normal));
        vec3 color=studio(reflect(incoming,normal))*entrance;
        vec3 ray=refract(incoming,normal,1.0/2.42),origin=vPosition+ray*uEpsilon;
        float weight=1.0-entrance;
        for(int bounce=0;bounce<4;bounce++){
          float distanceToFace=1.0e6;vec3 faceNormal=vec3(0.0,0.0,1.0);
          for(int i=0;i<72;i++){
            if(i>=uPlaneCount)break;
            vec4 plane=uPlanes[i];float denominator=dot(plane.xyz,ray);
            if(denominator>.00001){float t=-(dot(plane.xyz,origin)+plane.w)/denominator;if(t>uEpsilon*.1&&t<distanceToFace){distanceToFace=t;faceNormal=plane.xyz;}}
          }
          if(distanceToFace>100.0){color+=studio(ray)*weight;weight=0.0;break;}
          vec3 hit=origin+ray*distanceToFace,exitRay=refract(ray,-faceNormal,2.42);float reflection=1.0;
          if(dot(exitRay,exitRay)>.01){reflection=fresnel(dot(ray,faceNormal));color+=studio(exitRay)*weight*(1.0-reflection);}
          weight*=reflection;ray=reflect(ray,faceNormal);origin=hit+ray*uEpsilon;
        }
        color+=studio(ray)*weight;
        gl_FragColor=vec4(color,uOpacity);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`
  });
  return material;
}

export function createDiamondBeePart(shape,center){
  const design=designDiamondCuts(shape,center),group=new THREE.Group();group.name='shaped-diamond-bee';group.visible=false;
  for(const contour of design.cuts){
    const cut=buildShapedCut(contour);
    if(cut.planes.length>MAX_PLANES)throw new Error('Diamond cut exceeds optical plane limit');
    const material=crystalMaterial(cut),mesh=new THREE.Mesh(cut.geometry,material);
    mesh.name='fitted-diamond';mesh.position.set(cut.center.x,cut.center.y,.035);mesh.renderOrder=2;
    mesh.userData={contour,cut};
    mesh.onBeforeRender=()=>{material.uniforms.uInverse.value.copy(mesh.matrixWorld).invert();material.uniforms.uDirection.value.setFromMatrix4(mesh.matrixWorld);};
    group.add(mesh);
  }
  group.userData.design=design;
  group.userData.setOpacity=value=>group.children.forEach(m=>m.material.uniforms.uOpacity.value=value);
  return group;
}